/**
 * XOT beacon transport — fire-and-forget POST to /wp-json/xot/v1/signals
 * (or any configurable endpoint). Prefers `navigator.sendBeacon` so the
 * payload survives tab-close; falls back to `fetch({keepalive:true})` on
 * browsers without sendBeacon (still survives unload in modern engines).
 *
 * Replaces 3+ inline copies of the same try/sendBeacon/fetch-fallback
 * block scattered across the codebase:
 *   - single-article.js  → recordTTSPlay, _logDropoff
 *   - xot-signals.js     → flush()
 * Each had its own subtly different error handling. The lib gives one
 * canonical transport that all callers route through.
 *
 * Why a separate lib (not folded into the existing xot-signals.js IIFE):
 *   xot-signals.js is a high-level observability *consumer* (clicks +
 *   dwells + CWV). xot-beacon is the *transport primitive* underneath.
 *   Splitting them lets one-off callers (TTS play / dropoff in single-
 *   article.js) share the transport without pulling in 274 LOC of
 *   IntersectionObserver / PerformanceObserver / dwell-timer plumbing.
 *
 * Build path:
 *   src   assets/js/src/lib/xot-beacon.ts
 *   ↓ esbuild (IIFE, target ES2020)
 *   dist  assets/dist/js/lib/xot-beacon.js   (attaches window.XOT.beacon)
 *
 * Test path:
 *   src   assets/js/src/lib/xot-beacon.ts
 *   ↓ vitest (jsdom env, navigator + fetch mocked)
 *   test  tests/js/xot-beacon.test.ts
 *
 * @since Phase 2 — JS modernization, bite 5
 */

const DEFAULT_ENDPOINT = '/wp-json/xot/v1/signals';
const CONTENT_TYPE     = 'application/json';

/**
 * A single signal event payload. `type` is required; everything else is
 * optional but conventional — server schema accepts the shape directly.
 */
export interface SignalEvent {
    type: string;
    section?: string;
    item?: string;
    value?: number | null;
    entity?: string | number | null;
}

export interface SendOptions {
    /** REST endpoint, default `/wp-json/xot/v1/signals`. */
    endpoint?: string;
    /** WP REST nonce — sent as `?_wpnonce=…` for sendBeacon, `X-WP-Nonce` for fetch. */
    nonce?:    string;
    /** Originating URL recorded with the batch. Defaults to `location.pathname`. */
    url?:      string;
}

/**
 * Send one or more signal events to the analytics endpoint. Returns
 * `true` if the transport accepted the payload (sendBeacon succeeded, or
 * fetch kicked off without throwing). Returns `false` for empty input
 * or transport errors — caller does not need to handle the failure case
 * because signals are fire-and-forget.
 */
export function sendSignals(events: ReadonlyArray<SignalEvent>, opts: SendOptions = {}): boolean {
    if (!events || events.length === 0) return false;
    const endpoint = opts.endpoint || DEFAULT_ENDPOINT;
    const nonce    = opts.nonce    || '';
    const url      = opts.url ?? (typeof location !== 'undefined' ? location.pathname : '');
    const payload  = JSON.stringify({ signals: events, url });

    try {
        // Prefer sendBeacon — survives tab-close on every browser since 2017.
        if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
            const beaconUrl = nonce
                ? `${endpoint}?_wpnonce=${encodeURIComponent(nonce)}`
                : endpoint;
            const blob = new Blob([payload], { type: CONTENT_TYPE });
            return navigator.sendBeacon(beaconUrl, blob);
        }
        // Fallback — fetch with keepalive (~64KB per-origin limit, fine for analytics).
        if (typeof fetch === 'function') {
            const headers: Record<string, string> = { 'Content-Type': CONTENT_TYPE };
            if (nonce) headers['X-WP-Nonce'] = nonce;
            fetch(endpoint, { method: 'POST', headers, body: payload, keepalive: true })
                .catch(() => { /* silent — analytics never throw */ });
            return true;
        }
        return false;
    } catch {
        return false;
    }
}

/**
 * Convenience: send a single signal event. Equivalent to
 * `sendSignals([event], opts)`.
 */
export function sendSignal(event: SignalEvent, opts?: SendOptions): boolean {
    return sendSignals([event], opts);
}

/* ── window.XOT.beacon attachment (legacy IIFE consumers) ──────────── */
interface XotBeacon {
    send:    typeof sendSignals;
    sendOne: typeof sendSignal;
}

declare global {
    interface Window {
        XOT?: {
            beacon?: XotBeacon;
            [k: string]: unknown;
        };
    }
}

if (typeof window !== 'undefined') {
    const xot = (window.XOT ?? {}) as NonNullable<Window['XOT']>;
    xot.beacon = { send: sendSignals, sendOne: sendSignal };
    window.XOT = xot;
}
